// src/App.js
import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './lib/AuthContext';
import { SettingsProvider } from './lib/SettingsContext';
import './styles/global.css';
import './styles/accessibility.css';

// Pages
import SplashPage        from './pages/SplashPage';
import CountySelectPage  from './pages/CountySelectPage';
import TownSelectPage    from './pages/TownSelectPage';
import HomePage          from './pages/HomePage';
import EventDetailPage   from './pages/EventDetailPage';
import OrganiserProfilePage from './pages/OrganiserProfilePage';
import ServicesPage      from './pages/ServicesPage';
import ExplorePage       from './pages/ExplorePage';
import LoginPage         from './pages/LoginPage';
import SignupPage        from './pages/SignupPage';
import ProfilePage       from './pages/ProfilePage';
import BecomeOrganiserPage from './pages/BecomeOrganiserPage';
import TownAdminApplicationPage from './pages/TownAdminApplicationPage';
import MyTicketsPage     from './pages/MyTicketsPage';
import SettingsPage      from './pages/SettingsPage';

// Static pages
import AboutPage         from './pages/AboutPage';
import AccessibilityPage from './pages/AccessibilityPage';
import FAQPage           from './pages/FAQPage';
import ContactPage       from './pages/ContactPage';
import PrivacyPage       from './pages/PrivacyPage';
import SupportYourTownPage from './pages/SupportYourTownPage';
import SkillsPage        from './pages/SkillsPage';
import PostSkillPage     from './pages/PostSkillPage';
import SkillDetailPage   from './pages/SkillDetailPage';
import PostServicePage   from './pages/PostServicePage';
import EditServicePage   from './pages/EditServicePage';
import CommunityPage     from './pages/CommunityPage';
import CommunityDetailPage from './pages/CommunityDetailPage';

// Organiser
import PostEventPage      from './pages/organiser/PostEventPage';
import OrganiserDashboard from './pages/organiser/OrganiserDashboard';
import MyPostsPage from './pages/MyPostsPage';
import EditEventPage      from './pages/organiser/EditEventPage';
import BulkUploadPage    from './pages/organiser/BulkUploadPage';

// Admin
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminTowns     from './pages/admin/AdminTowns';
import AdminCounties  from './pages/admin/AdminCounties';
import AdminUsers     from './pages/admin/AdminUsers';
import AdminTownAdminApplications from './pages/admin/AdminTownAdminApplications';
import AdminEvents    from './pages/admin/AdminEvents';
import AdminScanner   from './pages/admin/AdminScanner';
import AdminServices  from './pages/admin/AdminServices';
import AdminSponsors  from './pages/admin/AdminSponsors';
import AdminSkills    from './pages/admin/AdminSkills';
import AdminCommunity from './pages/admin/AdminCommunity';
import AdminDeals     from './pages/admin/AdminDeals';
import AdminAnalytics from './pages/admin/AdminAnalytics';
import AdminAuditLog  from './pages/admin/AdminAuditLog';
import AdminImpactReport from './pages/admin/AdminImpactReport';
import AdminTownChecklist from './pages/admin/AdminTownChecklist';

// Route guards — roles from custom claims via AuthContext
const RequireAuth = ({ children }) => {
  const { user } = useAuth();
  return user ? children : <Navigate to="/login" replace />;
};
const RequireOrganiser = ({ children }) => {
  const { user, isOrganiser } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  if (!isOrganiser) return <Navigate to="/" replace />;
  return children;
};
// Editing a specific event: organisers editing their own, OR admin/town
// admin moderating — Firestore rules already enforce the real ownership/
// town-scoping at the data layer, this just controls who can reach the page.
const RequireCanEditEvent = ({ children }) => {
  const { user, isOrganiser, isAnyAdmin } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  if (!isOrganiser && !isAnyAdmin) return <Navigate to="/" replace />;
  return children;
};
const RequireAdmin = ({ children }) => {
  const { user, isAdmin } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  if (!isAdmin) return <Navigate to="/" replace />;
  return children;
};
// Town Champion admin OR full admin — used for pages that are safe to
// scope to one town (moderation, town-level content). Firestore rules
// are the real enforcement; this just controls who sees the page at all.
// Financial/system pages (Sponsors, Users, Analytics, Audit Log, Impact
// Report, Towns) use RequireAdmin above, never this one.
const RequireTownOrFullAdmin = ({ children }) => {
  const { user, isAnyAdmin } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  if (!isAnyAdmin) return <Navigate to="/" replace />;
  return children;
};

const registerSW = () => {
  if ('serviceWorker' in navigator) {
    window.addEventListener('load', () => {
      navigator.serviceWorker.register('/service-worker.js').catch(() => {});
    });
  }
};

const AppRoutes = () => (
  <Routes>
    {/* Onboarding */}
    <Route path="/welcome"                element={<SplashPage />} />
    <Route path="/county"                 element={<CountySelectPage />} />
    <Route path="/county/:countyId/towns" element={<TownSelectPage />} />

    {/* Main app */}
    <Route path="/"          element={<HomePage />} />
    <Route path="/services"  element={<ServicesPage />} />
    <Route path="/services/post" element={<RequireAuth><PostServicePage /></RequireAuth>} />
    <Route path="/services/edit/:id" element={<RequireAuth><EditServicePage /></RequireAuth>} />
    <Route path="/explore"   element={<ExplorePage />} />
    <Route path="/event/:id" element={<EventDetailPage />} />
    <Route path="/organisers/:organiserId" element={<OrganiserProfilePage />} />
    <Route path="/settings"  element={<SettingsPage />} />
    <Route path="/tickets"   element={<RequireAuth><MyTicketsPage /></RequireAuth>} />
    <Route path="/my-posts"  element={<RequireAuth><MyPostsPage /></RequireAuth>} />
    <Route path="/profile"   element={<ProfilePage />} />
    <Route path="/community" element={<CommunityPage />} />
    <Route path="/notice/:id" element={<CommunityDetailPage type="notice" />} />
    <Route path="/meetup/:id" element={<CommunityDetailPage type="meetup" />} />
    <Route path="/become-organiser" element={<RequireAuth><BecomeOrganiserPage /></RequireAuth>} />
    <Route path="/become-town-admin" element={<RequireAuth><TownAdminApplicationPage /></RequireAuth>} />

    {/* Static pages */}
    <Route path="/about"   element={<AboutPage />} />
    <Route path="/accessibility" element={<AccessibilityPage />} />
    <Route path="/faq"     element={<FAQPage />} />
    <Route path="/contact" element={<ContactPage />} />
    <Route path="/privacy"  element={<PrivacyPage />} />
    <Route path="/support-your-town" element={<SupportYourTownPage />} />
    <Route path="/skills"       element={<SkillsPage />} />
    <Route path="/skills/post"  element={<RequireAuth><PostSkillPage /></RequireAuth>} />
    <Route path="/skills/:id"   element={<SkillDetailPage />} />

    {/* Auth */}
    <Route path="/login"  element={<LoginPage />} />
    <Route path="/signup" element={<SignupPage />} />

    {/* Organiser */}
    <Route path="/post"               element={<RequireOrganiser><PostEventPage /></RequireOrganiser>} />
    <Route path="/organiser"          element={<RequireOrganiser><OrganiserDashboard /></RequireOrganiser>} />
    <Route path="/organiser/edit/:id" element={<RequireCanEditEvent><EditEventPage /></RequireCanEditEvent>} />
    <Route path="/organiser/bulk" element={<RequireOrganiser><BulkUploadPage /></RequireOrganiser>} />

    {/* Admin */}
    <Route path="/admin"          element={<RequireTownOrFullAdmin><AdminDashboard /></RequireTownOrFullAdmin>} />
    <Route path="/admin/towns"    element={<RequireAdmin><AdminTowns /></RequireAdmin>} />
    <Route path="/admin/counties" element={<RequireAdmin><AdminCounties /></RequireAdmin>} />
    <Route path="/admin/users"    element={<RequireAdmin><AdminUsers /></RequireAdmin>} />
    <Route path="/admin/town-admin-applications" element={<RequireAdmin><AdminTownAdminApplications /></RequireAdmin>} />
    <Route path="/admin/events"   element={<RequireTownOrFullAdmin><AdminEvents /></RequireTownOrFullAdmin>} />
    <Route path="/admin/scanner"  element={<RequireAdmin><AdminScanner /></RequireAdmin>} />
    <Route path="/admin/services" element={<RequireTownOrFullAdmin><AdminServices /></RequireTownOrFullAdmin>} />
    <Route path="/admin/sponsors" element={<RequireAdmin><AdminSponsors /></RequireAdmin>} />
    <Route path="/admin/skills"    element={<RequireTownOrFullAdmin><AdminSkills /></RequireTownOrFullAdmin>} />
    <Route path="/admin/community" element={<RequireTownOrFullAdmin><AdminCommunity /></RequireTownOrFullAdmin>} />
    <Route path="/admin/deals"     element={<RequireTownOrFullAdmin><AdminDeals /></RequireTownOrFullAdmin>} />
    <Route path="/admin/analytics" element={<RequireAdmin><AdminAnalytics /></RequireAdmin>} />
    <Route path="/admin/audit"     element={<RequireAdmin><AdminAuditLog /></RequireAdmin>} />
    <Route path="/admin/impact-report" element={<RequireAdmin><AdminImpactReport /></RequireAdmin>} />
    <Route path="/admin/launch-checklist" element={<RequireTownOrFullAdmin><AdminTownChecklist /></RequireTownOrFullAdmin>} />

    <Route path="*" element={<Navigate to="/" replace />} />
  </Routes>
);

const App = () => {
  useEffect(() => { registerSW(); }, []);

  // Referral capture — works no matter which page the link points to.
  // Stashed in localStorage since the person often browses a while, or
  // goes through Google's popup redirect, before actually signing up.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ref = params.get('ref');
    if (ref) {
      localStorage.setItem('gather-referral-code', ref);
      // Clean the URL so it doesn't stay in the address bar / get re-shared
      const url = new URL(window.location.href);
      url.searchParams.delete('ref');
      window.history.replaceState({}, '', url.toString());
    }
  }, []);

  return (
    <SettingsProvider>
      <AuthProvider>
        <BrowserRouter>
          <div className="app-container">
            <AppRoutes />
            <Toaster
              position="top-center"
              toastOptions={{
                duration: 3500,
                style: {
                  fontFamily: 'Nunito, sans-serif', fontWeight: 600,
                  fontSize: '14px', borderRadius: '12px',
                  background: '#2C342C', color: '#fff', maxWidth: '340px',
                },
                success: { iconTheme: { primary: '#AAC1AC', secondary: '#fff' } },
                error:   { iconTheme: { primary: '#C0392B', secondary: '#fff' } },
              }}
            />
          </div>
        </BrowserRouter>
      </AuthProvider>
    </SettingsProvider>
  );
};

export default App;
